import { buildPivotTableColumns } from './commonHelpers';
import { constructAgRequestFromHierarchy } from 'common/visualizations/helpers/ConstructAgRequest';
import { doClientSidePivot } from 'common/visualizations/helpers/PivotHelpers';
import { processConditionalCellStyles } from '../processConditionalCellStyles';
import { formatRows } from '../gridDataFormatter';
import { getForgeThemeColorConfig } from '../GridDataProcessor';
import { processGroupLevel } from './groupProcessor';
import { type SubtotalsData, type ExportData } from 'common/visualizations/views/agGridReact/types';
import { type GridApi } from 'ag-grid-community';
import { type NormalRow, type Row } from '../type';
import { type TableActionsResult, type ExtraOptions } from '../../types';
import {
  discoverPivotFieldsFromRows,
  populateSubtotalValues,
  createGroupKeyString,
  discoverPivotFieldsFromSubtotals,
} from './subtotalUtils';
import { filterRowsForExpansion } from '../groupExpansionFilter';

/**
 * Determines if a row should be shown at the top level in collapsed mode.
 */
export const isTopLevelRow = (row: Row): boolean =>
  row._rowType === 'total' || (row._rowType === 'header' && row._groupLevel === 0);

/**
 * Creates a lookup map of subtotal rows by their group keys for efficient access.
 */
export const createSubtotalMap = (
  pivotedRows: Row[],
  extraOptions: ExtraOptions,
  subtotalLookup?: SubtotalsData,
  groupColumnIds?: string[],
): Map<string, Row> => {
  const subtotalMap = new Map<string, Row>();

  if (!subtotalLookup?.size || !groupColumnIds?.length) {
    return subtotalMap;
  }

  // Collect header rows and discover pivot fields
  const headerRows: Row[] = [];
  for (const row of pivotedRows) {
    if (row._rowType === 'header' && row._groupKeys?.length) {
      headerRows.push(row);
    }
  }

  let allPivotFields = [];

  // Build subtotals from server lookup using hierarchy-based keys
  for (const row of headerRows) {
    const level = row._groupLevel ?? 0;
    const groupKeys = row._groupKeys;

    if (!groupKeys?.length) {
      continue;
    }

    const headerKey = createGroupKeyString(groupKeys, level);
    const subtotalRow = { ...row };

    // Populate server subtotals for available pivot fields
    // For pivot tables with inherit mode, we need to discover pivot fields for all levels
    // to ensure collapsed groups at deeper levels get their subtotal values

    const shouldUseSubtotalFields =
      extraOptions.featureFlags?.isTablePrintingImprovementGroupingLayoutEnabled &&
      extraOptions.featureFlags?.isTablePrintingImprovementInheritGroupExpansionEnabled &&
      (level === 0 || extraOptions.groupExpansion === 'inherit');
    if (shouldUseSubtotalFields) {
      allPivotFields = discoverPivotFieldsFromSubtotals(subtotalLookup, groupKeys, level);
    } else {
      allPivotFields = discoverPivotFieldsFromRows(pivotedRows);
    }
    populateSubtotalValues(subtotalRow, allPivotFields, groupKeys, level, subtotalLookup);

    subtotalMap.set(headerKey, subtotalRow);
  }

  return subtotalMap;
};

/**
 * Merges subtotal values into header rows based on matching group keys.
 * For inherit mode, only merges into collapsed groups.
 */
export const mergeSubtotalsIntoHeaders = (
  rows: Row[],
  subtotalMap: Map<string, Row>,
  extraOptions: ExtraOptions,
): Row[] => {
  if (subtotalMap.size === 0) {
    return rows;
  }
  const isGroupingLayoutEnabled = extraOptions.featureFlags?.isTablePrintingImprovementGroupingLayoutEnabled;
  const isInheritMode =
    extraOptions.featureFlags?.isTablePrintingImprovementInheritGroupExpansionEnabled &&
    extraOptions.groupExpansion === 'inherit';
  return rows.map((row) => {
    const rowTypeCondition = isGroupingLayoutEnabled
      ? ['total', 'subTotal'].includes(row._rowType)
      : row._rowType !== 'header';
    if (rowTypeCondition || !row._groupKeys?.length) {
      return row;
    }

    // For inherit mode, only processes collapsed groups
    if (isInheritMode && !row._isCollapsedGroup) {
      return row;
    }

    const level = row._groupLevel ?? 0;
    const key = createGroupKeyString(row._groupKeys, level);
    const matchingSubtotal = subtotalMap.get(key);

    if (!matchingSubtotal) {
      return row;
    }

    const updatedHeader = { ...row };
    for (const [fieldKey, value] of Object.entries(matchingSubtotal)) {
      if (!fieldKey.startsWith('_') && value != null) {
        updatedHeader[fieldKey] = value;
      }
    }

    return updatedHeader;
  });
};

/**
 * Processes raw tabular data into a pivoted table structure by organizing and grouping fetched rows on the client side,
 * without relying on ag-Grid’s built-in pivot functionality.
 * @param extraOptions
 * @param exportData
 * @param gridApi
 * @returns { pivotedRows: Row[]; pivotFields: string[] }
 */
export const pivotFromRawDataProcessor = (
  extraOptions: ExtraOptions,
  exportData: ExportData,
  gridApi: GridApi,
): { pivotedRows: Row[]; pivotFields: string[] } => {
  const { hierarchyConfig, showSubTotal } = extraOptions;
  const rows = exportData.rows as NormalRow[];

  if (!hierarchyConfig) {
    return { pivotedRows: rows, pivotFields: [] };
  }

  const fakeAgRequest = constructAgRequestFromHierarchy(hierarchyConfig);
  const { rowGroupCols } = fakeAgRequest;

  if (rowGroupCols.length === 0) {
    // When there are no row groups, use grand totals data if available since it's already aggregated correctly
    const { grandTotals } = exportData;
    const hasGrandTotals = grandTotals && grandTotals.length > 0;
    const normalizedGrandTotals = Array.isArray(grandTotals) ? grandTotals : [grandTotals];
    const dataToUse = hasGrandTotals ? normalizedGrandTotals : rows;
    const pivotResult = doClientSidePivot(fakeAgRequest, dataToUse);
    return {
      pivotedRows: pivotResult.pivotedRows as Row[],
      pivotFields: pivotResult.pivotFields,
    };
  }

  const pivotTableColumns = buildPivotTableColumns(gridApi, exportData, extraOptions);
  const groupColumnIds = rowGroupCols.map((col) => col.id);

  const isGroupingLayoutEnabled = extraOptions.featureFlags?.isTablePrintingImprovementGroupingLayoutEnabled;

  const context = {
    fakeAgRequest,
    rowGroupCols,
    groupColumnIds,
    showSubTotal,
    pivotTableColumns,
    subtotalLookup: exportData.subtotalLookup,
    isTablePrintingImprovementGroupingLayoutEnabled: isGroupingLayoutEnabled,
  };

  const { processedFields, processedRows } = processGroupLevel(rows, [], 0, context);

  return {
    pivotedRows: processedRows,
    pivotFields: processedFields,
  };
};

/**
 * Mirrors the live grid's hide-duplicate-groups behavior (Grid.tsx `isDuplicateGroupNode`):
 * a nested group whose value equals its parent group's value is hidden. Drops the header and
 * subtotal rows for such groups while keeping their leaf rows, so the printed output matches
 * the on-screen table. Non-group rows and top-level groups (level 0) are always kept.
 */
const removeRedundantGroupRows = (rows: Row[]): Row[] =>
  rows.filter((row) => {
    const level = row._groupLevel || -1;
    if (level <= 0 || !row._groupKeys) {
      return true;
    }

    return row._groupKeys[level] !== row._groupKeys[level - 1];
  });

/**
 * Returns the formatted rows that will be passed to the PDF workers for processing.
 * @param gridApi
 * @param exportData
 * @param extraOptions
 * @param pivotedRows
 * @param tableActionsResult
 * @returns
 */
export const finalizePivotedRows = (
  gridApi: GridApi,
  exportData: ExportData,
  extraOptions: ExtraOptions,
  pivotedRows: Row[],
  tableActionsResult: TableActionsResult,
) => {
  const pivotTableColumns = buildPivotTableColumns(gridApi, exportData, extraOptions);
  const { hierarchyConfig } = extraOptions;
  const { grandTotals } = exportData;

  const fakeAgRequest = hierarchyConfig ? constructAgRequestFromHierarchy(hierarchyConfig) : null;
  if (hierarchyConfig && fakeAgRequest) {
    const grandTotalsArray = grandTotals ? (Array.isArray(grandTotals) ? grandTotals : [grandTotals]) : [];
    const pivotGrandTotalRows = doClientSidePivot(fakeAgRequest, grandTotalsArray);
    if (extraOptions.showTotal && pivotGrandTotalRows.pivotedRows.length > 0) {
      pivotedRows.push({ ...pivotGrandTotalRows.pivotedRows[0], _rowType: 'total' });
    }
  }

  let finalRows = pivotedRows;

  // Handle cases where subtotal values should be merged into headers
  const shouldMergeSubtotals =
    extraOptions.groupExpansion === 'collapsed' ||
    (extraOptions.groupExpansion === 'expanded' && !extraOptions.showSubTotal);

  if (
    extraOptions.featureFlags?.isTablePrintingImprovementInheritGroupExpansionEnabled &&
    extraOptions.groupExpansion === 'inherit'
  ) {
    // Handle inherit mode - apply expansion state filtering for pivot tables
    const groupColumnIds =
      hierarchyConfig && fakeAgRequest ? fakeAgRequest.rowGroupCols.map((col) => col.id) : [];

    finalRows = filterRowsForExpansion(pivotedRows, extraOptions.expandedGroupsMap, {
      ...extraOptions,
      groupColumnIds,
    });

    // For inherit mode, also merge subtotals into collapsed header rows only
    if (hierarchyConfig && fakeAgRequest) {
      const subtotalMap = createSubtotalMap(
        pivotedRows,
        extraOptions,
        exportData.subtotalLookup,
        groupColumnIds,
      );
      finalRows = mergeSubtotalsIntoHeaders(finalRows, subtotalMap, extraOptions);
    }
  } else if (shouldMergeSubtotals && hierarchyConfig && fakeAgRequest) {
    const groupColumnIds = fakeAgRequest.rowGroupCols.map((col) => col.id);

    const subtotalMap = createSubtotalMap(
      pivotedRows,
      extraOptions,
      exportData.subtotalLookup,
      groupColumnIds,
    );

    if (extraOptions.groupExpansion === 'collapsed') {
      finalRows = pivotedRows.filter(isTopLevelRow);
    } else {
      finalRows = pivotedRows.filter((row) => row._rowType !== 'subTotal');
    }

    finalRows = mergeSubtotalsIntoHeaders(finalRows, subtotalMap, extraOptions);
  }
  if (extraOptions.featureFlags?.isTablePrintingImprovementHideDuplicateGroupingsEnabled) {
    finalRows = removeRedundantGroupRows(finalRows);
  }
  const formattedRowData = formatRows({
    rowData: processConditionalCellStyles(
      finalRows,
      pivotTableColumns,
      extraOptions.columnFormats,
      exportData.columns,
      extraOptions.showSubTotal,
      extraOptions.groupExpansion,
      extraOptions.featureFlags?.isTablePrintingImprovementGroupingLayoutEnabled,
      true, // isPivotTable
    ),
    tableColumns: pivotTableColumns,
    viewColumns: exportData.columns,
    extraOptions,
    gridApi,
  });

  return {
    rowData: formattedRowData,
    tableColumns: pivotTableColumns,
    tableActionsResult,
    tableDef: {
      rowHeight: Number(gridApi.getGridOption('rowHeight')) || 10,
    },
    processedRowCount: exportData.rows.length,
    forgeThemeColorConfig: getForgeThemeColorConfig(),
  };
};
